import axiosInstance from './axios';
import { token } from './auth';

const baseUrl = `/api/posts`;

const setConfig = (signal?: AbortSignal) => {
  return {
    headers: { 'x-auth-token': token },
    ...(signal ? { signal } : {}),
  };
};

/**
 * The home feed. The token rides along even though the route is public:
 * with it the server personalizes (joined herds + preference topics)
 * and filters the caller's hidden posts; without it, global newest-first.
 */
const getPosts = async (
  limit: number,
  page: number,
  signal?: AbortSignal
): Promise<any> => {
  const response = await axiosInstance.get(
    `${baseUrl}/?limit=${limit}&page=${page}`,
    setConfig(signal)
  );
  return response.data;
};

/** The explore feed — preference topics lead, everything else behind. */
const getExplorePosts = async (limit: number, page: number): Promise<any> => {
  const response = await axiosInstance.get(
    `${baseUrl}/explore?limit=${limit}&page=${page}`,
    setConfig()
  );
  return response.data;
};

const getSearchResults = async (
  query: string,
  limit: number,
  page: number
): Promise<any> => {
  // Token so the caller's hidden posts stay hidden in search too.
  const response = await axiosInstance.get(
    `${baseUrl}/search/?query=${query}&limit=${limit}&page=${page}`,
    setConfig()
  );
  return response.data;
};

/** Hide a post from the caller's feeds. */
const hidePost = async (id: string): Promise<any> => {
  const response = await axiosInstance.post(`${baseUrl}/${id}/hide`, {}, setConfig());
  return response.data;
};

const unhidePost = async (id: string): Promise<any> => {
  const response = await axiosInstance.delete(`${baseUrl}/${id}/hide`, setConfig());
  return response.data;
};

/**
 * Report a comment or a reply. Unlike a post report this hides nothing — you
 * objected to one remark, not to the whole discussion.
 */
const reportComment = async (
  postId: string,
  commentId: string,
  replyId: string | undefined,
  reason: string,
  detail?: string
): Promise<any> => {
  const path = replyId
    ? `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}/report`
    : `${baseUrl}/${postId}/comment/${commentId}/report`;
  const response = await axiosInstance.post(path, { reason, detail }, setConfig());
  return response.data;
};

/** Report a post. The server records it and hides it for the reporter. */
const reportPost = async (id: string, reason: string, detail?: string): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${id}/report`,
    { reason, detail },
    setConfig()
  );
  return response.data;
};

const addNew = async (postObj: any): Promise<any> => {
  const response = await axiosInstance.post(`${baseUrl}`, postObj, setConfig());
  return response.data;
};

const editPost = async (id: string, postObj: any): Promise<any> => {
  const response = await axiosInstance.patch(`${baseUrl}/${id}`, postObj, setConfig());
  return response.data;
};

const getPostComments = async (id: string): Promise<any> => {
  const response = await axiosInstance.get(`${baseUrl}/${id}/comments`);
  return response.data;
};

const upvotePost = async (id: string): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${id}/upvote`,
    null,
    setConfig()
  );
  return response.data;
};

const downvotePost = async (id: string): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${id}/downvote`,
    null,
    setConfig()
  );
  return response.data;
};

// Reactions use PUT (add) and DELETE (remove) rather than a toggle, so a retry
// after a dropped response can never flip the state back the other way.
const setReaction = async (
  id: string,
  reactionKey: string,
  add: boolean
): Promise<any> => {
  const url = `${baseUrl}/${id}/reactions/${reactionKey}`;
  const response = add
    ? await axiosInstance.put(url, null, setConfig())
    : await axiosInstance.delete(url, setConfig());
  return response.data;
};

const setCommentReaction = async (
  postId: string,
  commentId: string,
  reactionKey: string,
  add: boolean
): Promise<any> => {
  const url = `${baseUrl}/${postId}/comment/${commentId}/reactions/${reactionKey}`;
  const response = add
    ? await axiosInstance.put(url, null, setConfig())
    : await axiosInstance.delete(url, setConfig());
  return response.data;
};

const setReplyReaction = async (
  postId: string,
  commentId: string,
  replyId: string,
  reactionKey: string,
  add: boolean
): Promise<any> => {
  const url = `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}/reactions/${reactionKey}`;
  const response = add
    ? await axiosInstance.put(url, null, setConfig())
    : await axiosInstance.delete(url, setConfig());
  return response.data;
};

const deletePost = async (id: string): Promise<any> => {
  const response = await axiosInstance.delete(`${baseUrl}/${id}`, setConfig());
  return response.data;
};

const upvoteComment = async (postId: string, commentId: string): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment/${commentId}/upvote`,
    null,
    setConfig()
  );
  return response.data;
};

const downvoteComment = async (postId: string, commentId: string): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment/${commentId}/downvote`,
    null,
    setConfig()
  );
  return response.data;
};

const upvoteReply = async (
  postId: string,
  commentId: string,
  replyId: string
): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}/upvote`,
    null,
    setConfig()
  );
  return response.data;
};

const downvoteReply = async (
  postId: string,
  commentId: string,
  replyId: string
): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}/downvote`,
    null,
    setConfig()
  );
  return response.data;
};

const postComment = async (postId: string, commentObj: any): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment`,
    commentObj,
    setConfig()
  );
  return response.data;
};

const postReply = async (
  postId: string,
  commentId: string,
  replyObj: any
): Promise<any> => {
  const response = await axiosInstance.post(
    `${baseUrl}/${postId}/comment/${commentId}/reply`,
    replyObj,
    setConfig()
  );
  return response.data;
};

const updateComment = async (
  postId: string,
  commentId: string,
  commentObj: any
): Promise<any> => {
  const response = await axiosInstance.patch(
    `${baseUrl}/${postId}/comment/${commentId}`,
    commentObj,
    setConfig()
  );
  return response.data;
};

const removeComment = async (postId: string, commentId: string): Promise<any> => {
  const response = await axiosInstance.delete(
    `${baseUrl}/${postId}/comment/${commentId}`,
    setConfig()
  );
  return response.data;
};

const updateReply = async (
  postId: string,
  commentId: string,
  replyId: string,
  replyObj: any
): Promise<any> => {
  const response = await axiosInstance.patch(
    `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}`,
    replyObj,
    setConfig()
  );
  return response.data;
};

const removeReply = async (
  postId: string,
  commentId: string,
  replyId: string
): Promise<any> => {
  const response = await axiosInstance.delete(
    `${baseUrl}/${postId}/comment/${commentId}/reply/${replyId}`,
    setConfig()
  );
  return response.data;
};

const postService = {
  getPosts,
  getExplorePosts,
  hidePost,
  unhidePost,
  reportPost,
  reportComment,
  getSearchResults,
  addNew,
  editPost,
  getPostComments,
  upvotePost,
  downvotePost,
  setReaction,
  setCommentReaction,
  setReplyReaction,
  deletePost,
  upvoteComment,
  downvoteComment,
  upvoteReply,
  downvoteReply,
  postComment,
  postReply,
  updateComment,
  removeComment,
  updateReply,
  removeReply,
};

export default postService;
